import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  input,
  OnInit,
  output,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { EasyApplyJobOpportunity, EasyApplyRequiredFields } from '@models/easy-apply.model';
import { HowFindJobOpportunityEnum } from '@models/job-opportunity.models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  PoButtonModule,
  PoFieldModule,
  PoHelperModule,
  PoNotificationService,
  PoPageModule,
  PoToasterOrientation,
} from '@po-ui/ng-components';
import { ApplicantService } from '@services/applicant/applicant.service';
import { AuthService, FilesService, LoggedUser, UrlImagesService } from '@services/index';
import { AgreeTermsComponent } from '@shared/agree-terms/agree-terms.component';
import {
  AtsLibDatepickerComponent,
  AtsUiLibModule,
  Attachment,
  AttachmentRulesComponent,
  CustomValidators,
  DatepickerErrorMessageHelper,
  DateUtils,
  LabeledFormControl,
} from 'ats-ui-lib';
import { DateTime } from 'luxon';
import { lastValueFrom } from 'rxjs';
import { NoStandaloneSharedModule } from '../../no-standalone-shared/no-standalone-shared.module';
import { QrCodeTrackingCookieService } from '../../root/qr-code/qr-code-tracking-cookie.service';

export interface EasyApplyFormValue {
  fullName: string;
  birthday: Date | string | null;
  email: string;
  cellphone: string | null;
  linkedinUrl: string | null;
  cpf: string | null;
  salaryRange: {
    minimum: number | null;
    maximum: number | null;
  };
  attachmentFile: File;
}

@Component({
  selector: 'ats-easy-apply-forms',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PoFieldModule,
    PoButtonModule,
    PoPageModule,
    TranslateModule,
    AtsUiLibModule,
    NoStandaloneSharedModule,
    AtsLibDatepickerComponent,
    PoHelperModule,
    AttachmentRulesComponent,
  ],
  templateUrl: './easy-apply-forms.component.html',
  styleUrl: './easy-apply-forms.component.css',
})
export class EasyApplyFormsComponent implements OnInit {
  @ViewChild(AgreeTermsComponent) private termsConditionsModal?: AgreeTermsComponent;

  @Output() formSubmitted = new EventEmitter<EasyApplyFormValue>();

  public formStatusChanges = output<string>();

  public showFooter = input<boolean>(true);

  public enableNavigation = input<boolean>(true);

  public readonly FILE_SIZE_LIMIT = 2097152;

  public formCanceled = output<void>();

  public jobOpportunityId = input.required<string>();

  public tenantId = input.required<string>();

  public applicationSource = input<HowFindJobOpportunityEnum | null>(null);

  public companyName?: string | null;

  public companyLogoUrl?: string | URL;

  public companyLogoDescription?: string | null;

  public jobTitle?: string | null;

  public footerHeight = '0px';

  protected loggedUser = signal<LoggedUser | null>(null);

  public readonly datepickerErrorMessageHelper = new DatepickerErrorMessageHelper();

  readonly maxDate = DateTime.now().startOf('day').toJSDate();

  protected attachments = signal<Attachment[]>([]);

  public loadingForm = signal<boolean>(false);

  public sendingApplication = signal<boolean>(false);

  readonly formGroup = this.fb.group({
    fullName: new LabeledFormControl('l-full-name-registration', '', [
      CustomValidators.requiredIgnoreWhiteSpace,
      CustomValidators.fullNameSimple,
    ]),
    birthday: new LabeledFormControl('l-birthday', null, {
      validators: [CustomValidators.requiredIgnoreWhiteSpace, CustomValidators.validateBirthday],
      updateOn: 'change',
    }),
    email: new LabeledFormControl('l-email', '', [
      CustomValidators.requiredIgnoreWhiteSpace,
      CustomValidators.emailDomain,
    ]),
    cellphone: new LabeledFormControl('l-cellphone-number', null, [
      CustomValidators.requiredIgnoreWhiteSpace,
      CustomValidators.cellphone,
    ]),
    linkedinUrl: new LabeledFormControl('l-linkedin-url', null, {
      validators: CustomValidators.url,
    }),
    cpf: new LabeledFormControl('l-cpf', null, CustomValidators.cpf),
    salaryRange: this.fb.group(
      {
        minimum: new LabeledFormControl('l-salary-range', null, Validators.min(0)),
        maximum: new LabeledFormControl('l-salary-range', null, Validators.min(0)),
      },
      {
        validators: [CustomValidators.validateSalaryRange],
      },
    ),
    attachmentFile: new LabeledFormControl('l-attachments', null, {
      validators: [Validators.required],
    }),
  });

  get birthdayErrorMessage(): string {
    return this.datepickerErrorMessageHelper.resolveDateErrorMessage(
      this.formGroup.get('birthday'),
      'birthday',
    );
  }

  public cpfRequired = signal(false);

  public salaryRangeRequired = signal(false);

  constructor(
    private readonly fb: FormBuilder,
    private readonly applicantService: ApplicantService,
    private readonly router: Router,
    private readonly auth: AuthService,
    private readonly notificationService: PoNotificationService,
    private readonly translateService: TranslateService,
    private readonly attachmentService: FilesService,
    private readonly urlImagesService: UrlImagesService,
    private readonly qrCodeTrackingCookieService: QrCodeTrackingCookieService,
  ) {
    this.formGroup.statusChanges.pipe(takeUntilDestroyed()).subscribe((status) => {
      const isPending = this.formGroup.errors?.pending;
      this.formStatusChanges.emit(isPending ? 'PENDING' : status);
    });
  }

  async ngOnInit(): Promise<void> {
    if (!this.jobOpportunityId()) {
      return;
    }

    this.loadingForm.set(true);

    const jobOpportunity = await lastValueFrom(
      this.applicantService.getEasyApplyJobOpportunity(
        this.jobOpportunityId(),
        this.tenantId() ?? undefined,
      ),
    );

    this.setJobOpportunityInfo(jobOpportunity);

    const requiredFields = jobOpportunity.requiredFields ?? { cpf: false, salaryRange: false };
    this.applyRequiredFields(requiredFields);

    await this.initUserIfAuthenticated();

    this.loadingForm.set(false);
  }

  public async onSubmit(): Promise<void> {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    if (!this.jobOpportunityId()) {
      return;
    }

    try {
      this.sendingApplication.set(true);

      const formValue = this.formGroup.getRawValue() as EasyApplyFormValue;
      const formData = this.buildFormData(formValue);

      await this.createEasyApplyJobApplication(formData);

      this.formSubmitted.emit(formValue);

      if (this.enableNavigation()) await this.router.navigate(['/easy-apply/success']);
    } catch {
      await this.notifyErrorTryAgain();
    } finally {
      this.sendingApplication.set(false);
    }
  }

  private async createEasyApplyJobApplication(formData: FormData): Promise<void> {
    if (this.auth.isAuthenticated()) {
      await lastValueFrom(this.applicantService.createMyEasyApplyJobApplication(formData));
    } else {
      await lastValueFrom(this.applicantService.createEasyApplyJobApplication(formData));
    }
  }

  protected async notifyErrorTryAgain(): Promise<void> {
    const translations = this.translateService.instant(['l-error-try-again']);

    this.notificationService.error({
      message: translations['l-error-try-again'],
      orientation: PoToasterOrientation.Top,
    });
    this.sendingApplication.set(false);
  }

  public openTermsModal(): void {
    this.termsConditionsModal?.openModal();
  }

  public onFooterHeightChange(height: string): void {
    this.footerHeight = height;
  }

  public onCancel(): void {
    this.formCanceled.emit();
  }

  private async initUserIfAuthenticated(): Promise<void> {
    if (this.auth.isAuthenticated()) {
      this.setFormPending();
      try {
        await this.auth.init();
        this.loggedUser.set(this.auth.snapshot);
        this.autoFillEasyApply();
      } catch (ex) {
        if (ex instanceof HttpErrorResponse && ex.status === 403) return;
        throw ex;
      } finally {
        this.cleanFormPending();
      }
    }
  }

  private setFormPending(): void {
    const currentErrors = this.formGroup.errors || {};
    this.formGroup.setErrors({
      ...currentErrors,
      pending: true,
    });
  }

  private cleanFormPending(): void {
    const currentErrors = this.formGroup.errors || {};
    this.formGroup.setErrors({
      ...currentErrors,
      pending: false,
    });
  }

  protected autoFillEasyApply(): void {
    const loggedUser = this.loggedUser();

    if (loggedUser) {
      this.formGroup.controls.email.setValue(loggedUser.email);
      this.formGroup.controls.email.disable();
      this.formGroup.controls.fullName.setValue(loggedUser.name);
      this.formGroup.controls.birthday.setValue(loggedUser.birthday);
      this.formGroup.controls.cellphone.setValue(loggedUser.telephoneNumber);
      this.formGroup.controls.linkedinUrl.setValue(loggedUser.linkedinUrl);

      if (this.cpfRequired()) {
        this.formGroup.controls.cpf.setValue(loggedUser.cpf ?? null);
      }

      if (this.salaryRangeRequired()) {
        const salaryRange = loggedUser.salaryRange;
        const salaryRangeGroup = this.formGroup.get('salaryRange') as FormGroup;
        salaryRangeGroup.patchValue({
          minimum: salaryRange?.minimum ?? null,
          maximum: salaryRange?.maximum ?? null,
        });
      }

      this.initialResumeFile(loggedUser);
    }
  }

  private initialResumeFile(loggedUser: LoggedUser): void {
    if (loggedUser.resumeFile) {
      this.attachments.set([{ ...loggedUser.resumeFile, id: loggedUser.resumeFile.fileId }]);
      this.formGroup.controls.attachmentFile.setValue(loggedUser.resumeFile.fileId);
    }
  }

  private setJobOpportunityInfo(jobOpportunity: EasyApplyJobOpportunity): void {
    this.companyName = jobOpportunity.careerPage?.divulgationName ?? null;
    this.jobTitle = jobOpportunity.title;
    this.companyLogoUrl = this.urlImagesService.getCompanyLogo(
      jobOpportunity.careerPage.subdomain,
      jobOpportunity.careerPage.companyIconId,
    );
    this.companyLogoDescription = jobOpportunity.careerPage?.companyIconDescription ?? null;
  }

  private formatBirthday(date: Date | string | null): string {
    if (!date) {
      return '';
    }

    const birthday = date instanceof Date ? date.toISOString() : date;

    return DateUtils.transformPoDatePickerOutputToApiFormat(birthday);
  }

  private applyRequiredFields(requiredFields: EasyApplyRequiredFields): void {
    this.cpfRequired.set(requiredFields.cpf);
    this.salaryRangeRequired.set(requiredFields.salaryRange);

    const cpfControl = this.formGroup.controls.cpf;
    cpfControl.setValidators(
      requiredFields.cpf
        ? [CustomValidators.cpf, CustomValidators.requiredIgnoreWhiteSpace]
        : [CustomValidators.cpf],
    );
    cpfControl.updateValueAndValidity();

    const salaryRangeGroup = this.formGroup.get('salaryRange') as FormGroup;
    salaryRangeGroup.setValidators(
      requiredFields.salaryRange
        ? [CustomValidators.validateSalaryRange, this.salaryRangeRequiredValidator]
        : [CustomValidators.validateSalaryRange],
    );

    if (!requiredFields.salaryRange) {
      salaryRangeGroup.patchValue({ minimum: null, maximum: null });
    }

    salaryRangeGroup.updateValueAndValidity();

    if (!requiredFields.cpf) {
      this.formGroup.controls.cpf.setValue(null);
    }
  }

  public onSalaryRangeMinBlur(): void {
    const salaryRange = this.formGroup.get('salaryRange') as FormGroup | null;
    if (!salaryRange || salaryRange.invalid) return;
    const minimumControl = salaryRange.get('minimum');
    const maximumControl = salaryRange.get('maximum');
    if (minimumControl?.value && !maximumControl?.value) {
      maximumControl?.setValue(minimumControl.value);
    }
  }

  public onFileSelectChange(file: File): void {
    const clonedFile = new File([file], file.name, { type: file.type });
    this.formGroup.controls.attachmentFile.setValue(clonedFile);
  }

  public onDraftFilesChanges(files: ReadonlyArray<File>): void {
    const file = files[0];
    const clonedFile = file ? new File([file], file.name, { type: file.type }) : undefined;
    this.formGroup.controls.attachmentFile.setValue(clonedFile);
    this.formGroup.controls.attachmentFile.updateValueAndValidity();
  }

  private buildFormData(formValue: EasyApplyFormValue): FormData {
    const formData = new FormData();

    formData.append('jobOpportunityId', this.jobOpportunityId()!);
    formData.append('email', formValue.email.toLowerCase());
    formData.append('fullName', formValue.fullName);
    formData.append('birthday', this.formatBirthday(formValue.birthday));
    formData.append('telephoneNumber', formValue.cellphone ?? '');
    formData.append('linkedInUrl', formValue.linkedinUrl ?? '');

    if (this.cpfRequired()) {
      formData.append('cpf', formValue.cpf ?? '');
    }

    if (this.salaryRangeRequired()) {
      const salaryRangeMinimum = formValue.salaryRange?.minimum ?? 0;
      const salaryRangeMaximum = formValue.salaryRange?.maximum ?? 0;

      formData.append(
        'salaryRangeMinimum',
        salaryRangeMinimum > 0 ? String(salaryRangeMinimum) : '',
      );

      formData.append(
        'salaryRangeMaximum',
        salaryRangeMaximum > 0 ? String(salaryRangeMaximum) : '',
      );
    }

    formData.append('haveAgreedTermsConditions', String(true));
    if (formValue.attachmentFile && formValue.attachmentFile instanceof File) {
      formData.append('attachmentFile', formValue.attachmentFile, formValue.attachmentFile.name);
    }

    const qrCodeId = this.qrCodeTrackingCookieService.getQrCodeId(this.jobOpportunityId());
    formData.append('qrCodeId', qrCodeId ?? '');

    const applicationSource = this.applicationSource();
    if (applicationSource !== null && applicationSource !== undefined) {
      formData.append('applicationSource', String(applicationSource));
    }

    return formData;
  }

  public salaryRangeFilled(): boolean {
    const salaryRange = this.formGroup.get('salaryRange') as FormGroup | null;
    if (!salaryRange) return false;
    const minimum = Number(salaryRange.get('minimum')?.value ?? 0);
    const maximum = Number(salaryRange.get('maximum')?.value ?? 0);
    return minimum > 0 || maximum > 0;
  }

  private salaryRangeRequiredValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value as { minimum?: number | null; maximum?: number | null } | null;
    const minimum = Number(value?.minimum ?? 0);
    const maximum = Number(value?.maximum ?? 0);
    if (minimum > 0 || maximum > 0) {
      return null;
    }
    return { salaryRangeRequired: true };
  }

  public async onFileClicked(): Promise<void> {
    if (this.attachments().length > 0) {
      const file = this.attachments()[0];
      await this.attachmentService.openWithId(file.fileId);
    }
  }
}
